import { Button, Size, ControlType, Priority } from '@transferwise/components';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DeliveryEstimation from '../../../common/DeliveryEstimation';
import ExchangeRateIndicator from '../../../common/ExchangeRateIndicator';
import Label from '../../../common/Label';
import ProviderLogo from '../../../common/ProviderLogo';
import ReceiveAmount from '../../../common/ReceiveAmount';
import TransferFee from '../../../common/TransferFee';
import injectTheme from '../../../common/injectTheme';
import { isWiseAlias } from '../../../resources/aliasFilter';
import createUrl from '../../../resources/createUrl';
import { isCurrencyUnion } from '../../../resources/currency-unions';
import generateUrl, { AvailablePages } from '../../../resources/generateUrl';
import isConsideredMidmarketRate from '../../../resources/isConsideredMidmarketRate';
import { WISE_ALIAS } from '../../../resources/provider-constants';

import PricingDisclaimer from './PricingDisclaimer';
import TranslateInCountry from './TranslateInCountry';

function renderPricingDisclaimer(
  targetCountry,
  sourceCurrency,
  targetCurrency,
  alias,
  onClickToSeePricingDisclaimer,
) {
  return (
    <>
      {targetCountry && isCurrencyUnion(targetCurrency) && (
        <PricingDisclaimer
          messageKey="target-country-pricing-disclaimer"
          params={{
            inCountry: <TranslateInCountry code={targetCountry} />,
            sourceCurrency,
            targetCurrency,
            targetCountry,
          }}
          onClickToSeePricingDisclaimer={() => onClickToSeePricingDisclaimer(targetCountry, alias)}
        />
      )}
    </>
  );
}

// TODO Extract as component
function renderCallToAction(createClassNames, onCallToActionClick) {
  return (
    <div
      className={createClassNames(['btn-container', 'send-with-tw-cta_provider'])}
      data-testid="send-with-tw-cta"
    >
      <Button
        size={Size.SMALL}
        type={ControlType.POSITIVE}
        priority={Priority.PRIMARY}
        data-tracking-id="comparison-cta"
        onClick={onCallToActionClick}
        className={createClassNames(['send-with-tw-cta_provider'])}
      >
        <FormattedMessage id="send-money" />
      </Button>
    </div>
  );
}

const addDeliveryEstimation = (
  deliveryEstimation,
  isPartner,
  styles,
  alias,
  onClickingDeliveryEstimationTooltip,
  formatMessage,
) => (
  <>
    {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
    <td data-label={formatMessage({ id: 'should-arrive' })} className={styles['table-plain__cell']}>
      <DeliveryEstimation
        styles={styles}
        deliveryEstimation={deliveryEstimation}
        isPartner={isPartner}
        alias={alias}
        onClickingDeliveryEstimationTooltip={onClickingDeliveryEstimationTooltip}
      />
    </td>
  </>
);

const Provider = React.forwardRef(
  (
    {
      styles,
      createClassNames,
      alias,
      name,
      logo,
      logoAltText,
      source: sourceCurrency,
      target: targetCurrency,
      targetCountry,
      providerRate,
      midMarketRate,
      showDeliveryEstimation,
      deliveryEstimation,
      amount,
      fees,
      differenceToCheapest,
      isPartner,
      onCallToActionClick,
      onClickToSeePricingDisclaimer,
      onClickingDeliveryEstimationTooltip,
      showSendMoneyButton,
      providerLogoRedirectEnabled,
      onProviderClick,
      isCentre,
      isScreenModeDark,
      providersWithDisclaimers,
    },
    reference,
  ) => {
    const { formatMessage, locale } = useIntl();
    const MID_MARKET_URL = createUrl(
      generateUrl(locale, 'mid-market-rate/', AvailablePages.MID_MARKET_RATE),
    );
    const WISE_FEES_URL = createUrl(
      generateUrl(locale, 'pricing/send-money', AvailablePages.PRICING),
    );
    const getProviderLogoUrl = () => {
      if (providerLogoRedirectEnabled && alias !== WISE_ALIAS) {
        const path = 'compare/' + alias + '-exchange-rate';
        return createUrl(generateUrl(locale, path, AvailablePages.PROVIDER_PAGE));
      }
    };
    return (
      <tr
        className={`${styles['table-plain__row']}
          ${isScreenModeDark ? styles[isScreenModeDark ? 'table-plain__row--dark' : ''] : ''}
        `}
        data-testid={isCentre ? 'centre-provider-row' : 'provider-row'}
        ref={reference}
      >
        <td className={styles['table-plain__cell']}>
          <ProviderLogo
            logo={logo}
            alias={alias}
            altText={logoAltText}
            isPartner={isPartner}
            providerLogoRedirectUrl={getProviderLogoUrl()}
            onProviderClick={() => onProviderClick(name)}
            providersWithDisclaimers={providersWithDisclaimers}
          />
        </td>
        {showDeliveryEstimation
          ? addDeliveryEstimation(
              deliveryEstimation,
              isPartner,
              styles,
              alias,
              onClickingDeliveryEstimationTooltip,
              formatMessage,
            )
          : ''}

        <td
          className={styles['table-plain__cell']}
          data-label={formatMessage({ id: 'exchange-rate' })}
        >
          {/* TODO: This ExchangeRateIndicator component with midmarket rate label should be extracted out */}
          <ExchangeRateIndicator
            styles={styles}
            referenceRate={midMarketRate}
            rate={providerRate}
            label={
              isConsideredMidmarketRate(providerRate, midMarketRate) ? (
                <Label
                  styles={styles}
                  message="mid-market-rate"
                  link={MID_MARKET_URL}
                  target="_blank"
                />
              ) : null
            }
            sourceCurrency={sourceCurrency}
            targetCurrency={targetCurrency}
          />
        </td>

        <td
          className={styles['table-plain__cell']}
          data-label={formatMessage({ id: 'transfer-fee' })}
        >
          <TransferFee
            styles={styles}
            currency={sourceCurrency}
            fee={fees}
            alias={alias}
            label={
              isWiseAlias(alias) || isPartner ? ( // TODO Condition could actually be where any rate >= than midmarket
                <Label
                  styles={styles}
                  message="transparent-fee"
                  link={WISE_FEES_URL}
                  target="_blank"
                />
              ) : null
            }
          />
        </td>

        <td
          className={createClassNames(['table-plain__cell', 'featured--last-cell'])}
          data-label={formatMessage({ id: 'you-receive' })}
        >
          <ReceiveAmount
            styles={styles}
            amount={amount}
            currency={targetCurrency}
            differenceToCheapest={parseFloat(differenceToCheapest)}
            optionalInlineElement={renderPricingDisclaimer(
              targetCountry,
              sourceCurrency,
              targetCurrency,
              alias,
              onClickToSeePricingDisclaimer,
            )}
          />
          {showSendMoneyButton &&
            isWiseAlias(alias) &&
            renderCallToAction(createClassNames, onCallToActionClick)}
        </td>
      </tr>
    );
  },
);

Provider.propTypes = {
  styles: PropTypes.shape({
    'pull-xs-left': PropTypes.string,
    'text-xs-right': PropTypes.string,
    'table-plain__row': PropTypes.string,
    'table-plain__row--dark': PropTypes.string,
    'table-plain__cell': PropTypes.string,
  }),
  createClassNames: PropTypes.func.isRequired,
  alias: PropTypes.string.isRequired,
  logo: PropTypes.string.isRequired,
  logoAltText: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  targetCountry: PropTypes.string,
  providerRate: PropTypes.number.isRequired,
  midMarketRate: PropTypes.number.isRequired,
  showDeliveryEstimation: PropTypes.bool,
  deliveryEstimation: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    duration: PropTypes.object,
    providerGivesEstimate: PropTypes.bool,
  }),
  amount: PropTypes.number.isRequired,
  fees: PropTypes.number.isRequired,
  differenceToCheapest: PropTypes.number.isRequired,
  isPartner: PropTypes.bool.isRequired,

  onCallToActionClick: PropTypes.func,
  onClickToSeePricingDisclaimer: PropTypes.func,
  onClickingDeliveryEstimationTooltip: PropTypes.func,
  showSendMoneyButton: PropTypes.bool,
  providerLogoRedirectEnabled: PropTypes.bool,
  onProviderClick: PropTypes.func,
  isCentre: PropTypes.bool,
  isScreenModeDark: PropTypes.bool,
  providersWithDisclaimers: PropTypes.arrayOf(
    PropTypes.shape({
      alias: PropTypes.string.isRequired,
      disclaimer: PropTypes.element.isRequired,
    }),
  ),
};

Provider.defaultProps = {
  styles: {},
  targetCountry: undefined,
  onCallToActionClick: () => {},
  onClickToSeePricingDisclaimer: undefined,
  showDeliveryEstimation: false,
  deliveryEstimation: { duration: null, providerGivesEstimate: true },
  onClickingDeliveryEstimationTooltip: () => {},
  showSendMoneyButton: true,
  providerLogoRedirectEnabled: false,
  providerLogoRedirectUrl: null,
  onProviderClick: () => {},
  isCentre: false,
  isScreenModeDark: false,
};

export default injectTheme(Provider);
